import type { ChatConfiguration } from '@/stores/chatBuilderStore'

const chatBaseUrl = import.meta.env.VITE_CHAT_BASE_URL

const isDevelopment = process.env.NODE_ENV === 'development'

export const vwamScriptUrl = isDevelopment
  ? `${chatBaseUrl}/vwam.js`
  : `${chatBaseUrl}/vwam.min.js`

// Placeholder for the merchant's own product-ID variable in the universal
// slot. Kept as an obvious templating token (not a scary REPLACE_WITH…) so it
// reads as "swap in your platform's product-id expression here".
export const UNIVERSAL_PRODUCT_REF_PLACEHOLDER = 'YOUR_PRODUCT_ID'

type DeploymentCodeConfig = Pick<
  ChatConfiguration,
  'shortKey' | 'deploymentType'
>

export interface DeploymentCodes {
  script: string
  container: string
  chatUrl: string
  deploymentType: string
  // Present only for pdp_qa: the universal paste-in mount point (product ref is
  // a templating placeholder the merchant substitutes).
  slot?: string
  // Present only for pdp_qa: the Shopify theme-app-block Liquid, where the ref
  // is injected automatically from {{ product.id }} — no manual step.
  shopifyLiquid?: string
  // Present only for pdp_qa: the universal loader carrying the short key on data-vwam-pdp-config, never data-vwam-config/data-vwam-launch-type.
  universalScript?: string
}

/**
 * Generates deployment codes for a configuration based on its deployment type
 * @param config - The chat configuration
 * @param organizationId - The signed-in organization; list payloads do not carry one
 * @returns Object with appropriate deployment codes
 */
export function generateDeploymentCodes(
  config: DeploymentCodeConfig,
  type: string = config.deploymentType ?? 'full_screen',
  organizationId?: string
): DeploymentCodes {
  // The loader script is ALWAYS the same generic tag, regardless of deployment
  // type. Launch type is decided per-mount by the slot/container div, never by
  // the script — so one script on a site can serve chat AND Product Q&A.

  // data-vwam-org drives loader page-view capture; omitted when the org id is
  // absent, so an install without it keeps working and simply captures nothing.
  const orgAttribute = organizationId
    ? ` data-vwam-org="${organizationId}"`
    : ''
  const script = `<script src="${vwamScriptUrl}"${orgAttribute} async></script>`
  const chatUrl = `${chatBaseUrl}/${config.shortKey}`

  if (type === 'widget') {
    const widgetScriptUrl = `${chatBaseUrl}/vwam-widget${isDevelopment ? '' : '.min'}.js`

    return {
      script: `<script src="${widgetScriptUrl}" data-vwam-widget-config="${config.shortKey}" async></script>`,
      container: '',
      chatUrl,
      deploymentType: type,
    }
  }

  // pdp_qa has three install paths: universal auto-detect script, an explicit paste-in slot, and Shopify's app block.
  if (type === 'pdp_qa') {
    const universalScript = `<script src="${vwamScriptUrl}" data-vwam-pdp-config="${config.shortKey}"${orgAttribute} async></script>`
    const slot = `<div data-vwam-pdp-slot data-vwam-product-ref="${UNIVERSAL_PRODUCT_REF_PLACEHOLDER}"></div>`
    const shopifyLiquid = `<div data-vwam-pdp-slot data-vwam-launch-type="pdp_qa" data-vwam-config="${config.shortKey}" data-vwam-product-ref="shopify-{{ product.id }}" data-vwam-product-handle="{{ product.handle }}"></div>`

    return {
      script,
      universalScript,
      slot,
      shopifyLiquid,
      chatUrl,
      container: slot,
      deploymentType: type,
    }
  }

  const container = `<div class="vwam-chat-${config.shortKey}" data-vwam-config="${config.shortKey}" width="100%" height="600px"></div>`

  return {
    script,
    chatUrl,
    container,
    deploymentType: type,
  }
}

/**
 * Gets the main deployment code string for display purposes
 * @param config - The chat configuration
 * @param organizationId - The signed-in organization; list payloads do not carry one
 * @returns The primary deployment code as a string
 */
export function getDeploymentCodeString(
  config: DeploymentCodeConfig,
  organizationId?: string
): string {
  const codes = generateDeploymentCodes(config, undefined, organizationId)

  if (codes.deploymentType === 'pdp_qa') {
    return codes.universalScript ?? codes.container
  }

  if (codes.deploymentType === 'embed') {
    return codes.container
  }

  if (codes.deploymentType === 'widget') {
    return codes.script
  }

  return codes.chatUrl
}
